/*
  Alyaka — native gift-with-purchase auto-add (job/gwp-auto-add, 2026-09-30; Gift offer entries: job/gift-offers)
  Config: window.ALY_GWP from Gift offer metaobject entries, window.ALY_GWP_LEGACY from shop metafield
  custom.gwp_offers (fallback) — both built in snippets/aly-gwp-auto.liquid.
  Pricing: the gift product itself is priced £0 and tagged gwp-original (option C, 2026-09-30 — no Shopify
  discount, so checkout stays clean like BOGOS). This script adds the gift when the cart qualifies and removes
  it when it does not; the safety net still removes a gift that comes back charged.
  Gift lines carry the hidden property `_gwp` = offer key (underscore = not shown at checkout).
*/
(() => {
  // Gift offer entries (metaobject) first; legacy JSON offers only for gift variants that have no entry
  const valid = o => o && o.key && o.gift_variant_id;
  const MAIN = Array.isArray(window.ALY_GWP) ? window.ALY_GWP.filter(valid) : [];
  const seen = new Set(MAIN.map(o => Number(o.gift_variant_id)));
  const LEGACY = (Array.isArray(window.ALY_GWP_LEGACY) ? window.ALY_GWP_LEGACY.filter(valid) : [])
    .filter(o => !seen.has(Number(o.gift_variant_id)));
  const OFFERS = MAIN.concat(LEGACY);
  if (!OFFERS.length || window.__alyGwpAuto) return;
  window.__alyGwpAuto = true;

  const PROP = '_gwp';
  const DISMISS = 'aly-gwp-x-';        // sessionStorage: customer removed the gift → do not re-add this session
  const MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|#|$)/;
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const MINE = 'X-Aly-Gwp';

  const post = (path, body) => fetch(root + path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', [MINE]: '1' },
    body: JSON.stringify(body)
  });
  // Add a gift line; retry when Shopify rejects the write because the cart is busy (409 conflict,
  // 429 throttle, 5xx, network error) — up to 3 attempts, 600 / 1200 ms apart. 422 (sold out) is final.
  async function addGift(o) {
    let r = null;
    for (let n = 0; n < 3; n++) {
      if (n) await new Promise(res => setTimeout(res, 600 * n));
      // Re-read the cart right before every attempt, the first one too: another tab, a page that was just
      // left, or a 409 that arrived after the add landed may already have put the gift in (#37749, #38183)
      try {
        const c = await getCart();
        if (c.items.some(i => isGift(i) === o.key)) { log('add-already-in-cart', o.key); return { ok: true, status: 'present' }; }
      } catch (e) {}
      lock.take();   // refresh our lock timestamp so a long retry chain keeps other tabs waiting
      try { r = await post('cart/add.js', { items: [{ id: Number(o.gift_variant_id), quantity: 1, properties: { [PROP]: o.key } }] }); }
      catch (e) { r = { ok: false, status: 'net' }; }
      if (r.ok || !(r.status === 409 || r.status === 429 || r.status === 'net' || r.status >= 500)) break;
      log('add-busy-' + r.status, o.key);
    }
    return r;
  }
  const getCart = () => fetch(root + 'cart.js', { credentials: 'same-origin', headers: { [MINE]: '1' } }).then(r => r.json());
  const ss = {
    get: k => { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set: k => { try { sessionStorage.setItem(k, '1'); } catch (e) {} }
  };
  // A line is a gift when it carries _gwp, or when it is an offer's gift variant added any other way
  // (direct URL / old link): the gift product is priced £0, so it must never stay without a qualifying cart.
  // Several offers may share one gift product: a line belongs to the offer named in its _gwp property; only a line
  // without a known key (old key, direct URL) is matched by variant, to the first offer using that gift.
  const KEYS = new Set(OFFERS.map(o => o.key));
  const GIFT_BY_VARIANT = {};
  OFFERS.forEach(o => { const v = Number(o.gift_variant_id); if (!GIFT_BY_VARIANT[v]) GIFT_BY_VARIANT[v] = o.key; });
  const isGift = i => {
    const p = i.properties && i.properties[PROP];
    if (p && KEYS.has(p)) return p;
    return GIFT_BY_VARIANT[i.variant_id] || p || '';
  };
  // Small ring buffer for support/debugging: window.ALY_GWP_LOG (last 40 actions)
  const LOG = window.ALY_GWP_LOG = [];
  const log = (a, k) => { LOG.push([new Date().toISOString().slice(11, 23), a, k || '']); if (LOG.length > 40) LOG.shift(); };

  const qualifies = (o, cart) => {
    // Qualifying lines: any paid product (condition "Any order"), the brand's products (vendor,
    // case-insensitive) or a legacy product-id list. Lines priced £0 (any gift, incl. BOGOS clones) never count.
    const ids = (o.buy_product_ids || []).map(Number);
    const vendor = String(o.vendor || '').trim().toLowerCase();
    const lines = cart.items.filter(i => !isGift(i) && i.original_price > 0 &&
      (o.any ? true : ids.length ? ids.includes(i.product_id) : (vendor && String(i.vendor || '').trim().toLowerCase() === vendor)));
    const qty = lines.reduce((s, i) => s + i.quantity, 0);
    const amt = lines.reduce((s, i) => s + i.final_line_price, 0) / 100;
    const minQty = Number(o.min_qty) || 0;
    const minAmt = Number(o.min_amount) || 0;
    return (minQty ? qty >= minQty : qty > 0) && amt >= minAmt;
  };

  // Re-render the Minimog cart drawer, cart page and header count after our own changes
  const refreshUi = cart => {
    try {
      const drawer = document.querySelector('m-cart-drawer');
      if (drawer && typeof drawer.onCartDrawerUpdate === 'function') drawer.onCartDrawerUpdate();
      const ev = window.MinimogEvents, names = window.MinimogTheme && MinimogTheme.pubSubEvents;
      if (ev && names && names.cartUpdate) ev.emit(names.cartUpdate, { cart, source: 'aly-gwp' });
      document.querySelectorAll('.m-cart-count-bubble').forEach(b => {
        if (cart.item_count > 0) { b.textContent = cart.item_count; b.classList.remove('m:hidden'); }
        else b.classList.add('m:hidden');
      });
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('aly:gwp:changed', { detail: { cart } }));
  };

  // Cross-tab lock: two open tabs, or a page being left while the next one loads, must not add the same gift
  // at once (each sees "no gift yet" → ×2). Held while sync() runs and refreshed before every add; it expires
  // after 4 s, so a closed tab never blocks the others and a page that was just left gets time for its add to land.
  const TAB = Math.random().toString(36).slice(2);
  const LOCK = 'aly-gwp-lock';
  const lock = {
    read() { try { return JSON.parse(localStorage.getItem(LOCK) || 'null'); } catch (e) { return null; } },
    take() {
      try {
        const cur = lock.read();
        if (cur && cur.id !== TAB && Date.now() - cur.t < 4000) return false;
        localStorage.setItem(LOCK, JSON.stringify({ id: TAB, t: Date.now() }));
        const now = lock.read();
        return !now || now.id === TAB;
      } catch (e) { return true; }   // no storage (private mode) → behave as before
    },
    drop() { try { const cur = lock.read(); if (cur && cur.id === TAB) localStorage.removeItem(LOCK); } catch (e) {} }
  };

  // blocked: offers whose gift came back charged (discount off / not matching) — never re-added on this
  // page load, otherwise add → safety-remove → add would loop on every cart event
  let busy = false, again = false, lastGifts = {}, blocked = {};
  async function sync() {
    if (busy) { again = true; return; }
    if (!lock.take()) { log('wait-other-tab'); setTimeout(schedule, 1200); return; }
    busy = true;
    try {
      let cart = await getCart(), changed = false;
      const added = [];
      for (const o of OFFERS) {
        const lines = cart.items.filter(i => isGift(i) === o.key);
        // Shopify splits a gift line (1 free + N charged) when its quantity is raised: keep one line, drop the rest
        const gift = lines.find(i => i.final_line_price === 0) || lines[0];
        for (const x of lines) {
          if (x !== gift) { await post('cart/change.js', { id: x.key, quantity: 0 }); changed = true; log('remove-extra-line', o.key); }
        }
        const ok = qualifies(o, cart);
        // the gift we added earlier is gone while the cart still qualifies → the customer removed it
        if (!gift && lastGifts[o.key] && ok) { ss.set(DISMISS + o.key); log('dismissed-by-customer', o.key); }
        if (ok && !gift && !blocked[o.key] && !ss.get(DISMISS + o.key)) {
          const r = await addGift(o);
          if (r.ok) { changed = true; added.push(o); }   // 422 (sold out / unavailable) → skip silently
          log(r.ok ? 'add' : 'add-failed-' + r.status, o.key);
        } else if (gift && (!ok || gift.quantity !== 1)) {
          await post('cart/change.js', { id: gift.key, quantity: ok ? 1 : 0 });
          changed = true;
          log(ok ? 'qty-reset' : 'remove-not-qualified', o.key);
        }
      }
      // Orphans: a _gwp gift whose offer ended / was removed while it sat in the cart
      const keys = new Set(OFFERS.map(o => o.key));
      for (const i of cart.items) {
        if (i.properties && i.properties[PROP] && !keys.has(isGift(i))) {
          await post('cart/change.js', { id: i.key, quantity: 0 }); changed = true; log('remove-orphan', i.properties[PROP]);
        }
      }
      if (changed) cart = await getCart();
      // A concurrent cart write (drawer / app) can overwrite our add: if an added gift is missing, retry once
      const lost = added.filter(o => !cart.items.some(i => isGift(i) === o.key));
      if (lost.length) {
        await new Promise(r => setTimeout(r, 800));
        for (const o of lost) {
          const r = await addGift(o);
          log(r.ok ? 'add-retry' : 'add-retry-failed-' + r.status, o.key);
        }
        cart = await getCart();
      }
      // Safety net: never leave a charged auto-added gift (discount off, expired or not matching).
      // cart.js can briefly return the gift at full price right after a mutation, so re-read once
      // after 1.5 s before removing; a gift removed here is not re-added on this page load.
      let removed = false;
      if (cart.items.some(i => isGift(i) && i.final_line_price > 0)) {
        await new Promise(r => setTimeout(r, 1500));
        cart = await getCart();
        for (const i of cart.items) {
          if (isGift(i) && i.final_line_price > 0) {
            const k = isGift(i);
            const freeTwin = cart.items.some(x => x !== i && isGift(x) === k && x.final_line_price === 0);
            if (!freeTwin) blocked[k] = true;   // the discount really is not applying → stop re-adding
            log(freeTwin ? 'remove-extra-line' : 'removed-charged-gift', k);
            await post('cart/change.js', { id: i.key, quantity: 0 });
            removed = true;
          }
        }
      }
      if (removed) { cart = await getCart(); changed = true; }
      // Final guard: a gift line is always quantity 1 (a retried add or a concurrent write can leave 2)
      let fixedQty = false;
      for (const i of cart.items) {
        if (isGift(i) && i.quantity > 1) {
          await post('cart/change.js', { id: i.key, quantity: 1 });
          log('qty-fixed-' + i.quantity, isGift(i));
          fixedQty = true;
        }
      }
      if (fixedQty) { cart = await getCart(); changed = true; }
      lastGifts = {};
      cart.items.forEach(i => { if (isGift(i)) lastGifts[isGift(i)] = true; });
      if (changed) refreshUi(cart);
    } catch (e) { /* never break the cart */ }
    finally {
      lock.drop();
      busy = false;
      if (again) { again = false; setTimeout(sync, 50); }
      else document.dispatchEvent(new CustomEvent('aly:gwp:synced'));
    }
  }

  // Checkout button (cart drawer + /cart, both name="checkout"): run one gift check first (max 2 s), then
  // submit. The form also posts updates[] for every line, so gift-line inputs are set to 1 before it goes —
  // a stale drawer can never send a gift back as 2. Express buttons (Apple Pay, Shop Pay, PayPal) are
  // Shopify iframes and cannot be held; they still rely on the passes above.
  const checkNow = () => new Promise(res => {
    let done = false;
    const end = () => { if (done) return; done = true; document.removeEventListener('aly:gwp:synced', end); res(); };
    document.addEventListener('aly:gwp:synced', end);
    setTimeout(end, 2000);
    sync();
  });
  let passing = false;
  document.addEventListener('submit', e => {
    const b = e.submitter, form = e.target;
    if (passing || !b || b.name !== 'checkout' || !form || !form.querySelectorAll) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    b.setAttribute('aria-busy', 'true');
    checkNow().then(() => {
      document.querySelectorAll('.aly-gwp-line input[name^="updates["]').forEach(i => {
        if (i.form === form && Number(i.value) !== 1) { i.value = 1; log('checkout-input-fixed'); }
      });
      b.removeAttribute('aria-busy');
      log('checkout-go');
      passing = true;
      setTimeout(() => { passing = false; }, 3000);
      try { form.requestSubmit(b); } catch (err) { window.location.href = root + 'checkout'; }
    });
  }, true);

  const schedule = (() => { let t; return () => { clearTimeout(t); t = setTimeout(sync, 150); }; })();
  const hasMine = init => {
    const h = init && init.headers;
    if (!h) return false;
    if (typeof h.get === 'function') return !!h.get(MINE);
    return !!h[MINE];
  };

  // Run after any cart mutation made by the theme or apps (not by this script)
  const origFetch = window.fetch;
  window.fetch = function (input, init) {
    const p = origFetch.apply(this, arguments);
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || String(input);
      if (!hasMine(init) && MUTATION_RE.test(url)) p.then(schedule, schedule);
    } catch (e) {}
    return p;
  };
  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    try { if (MUTATION_RE.test(String(url))) this.addEventListener('loadend', schedule); } catch (e) {}
    return origOpen.apply(this, arguments);
  };
  // Cart restored from another tab / back-forward cache
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') schedule(); });
  window.addEventListener('pageshow', e => { if (e.persisted) schedule(); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync, { once: true });
  else sync();
})();
